import { useEffect, useRef } from "react";
import "./Ants.css";

const ANT_COUNT_DESKTOP = 37;
const ANT_COUNT_MOBILE = 16;
const MOBILE_BREAKPOINT = 768;
const getAntCount = () =>
  window.innerWidth <= MOBILE_BREAKPOINT ? ANT_COUNT_MOBILE : ANT_COUNT_DESKTOP;
const AVOID_RADIUS = 46;
const AVOID_TURN = 0.05;
const WANDER = 0.012;
const SPEED_MIN = 0.18;
const SPEED_MAX = 0.42;
const MOUSE_RADIUS = 140;
const MOUSE_TURN = 0.14;
const MOUSE_SPEED_BOOST = 0.55;

const ANT_COLORS = [
  [60, 60, 65],
  [105, 70, 42],
  [170, 75, 40],
  [200, 55, 40],
  [240, 170, 55],
  [210, 175, 130],
  [236, 176, 43],
  [90, 170, 95],
  [140, 130, 220],
  [220, 220, 225],
  [255, 122, 61],
  [255, 61, 104],
  [255, 210, 70],
  [130, 220, 120],
  [60, 200, 180],
  [70, 170, 240],
  [80, 120, 235],
  [140, 90, 220],
  [200, 90, 230],
  [255, 120, 190],
  [255, 80, 80],
];

const EMBER_EMIT_CHANCE = 0.25;
const EMBER_LIFE_MIN = 20;
const EMBER_LIFE_MAX = 38;
const MAX_EMBERS = 220;

const rand = (a, b) => a + Math.random() * (b - a);

const Ants = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const antsRef = useRef([]);
  const embersRef = useRef([]);
  const themeRef = useRef("dark");

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const readTheme = () => {
      themeRef.current = document.documentElement.dataset.theme === "light" ? "light" : "dark";
    };
    readTheme();
    const themeObs = new MutationObserver(readTheme);
    themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    let width = 0;
    let height = 0;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const onResize = () => {
      resize();
      syncCount();
    };
    window.addEventListener("resize", onResize);

    const mouse = { x: -9999, y: -9999, active: false };
    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseout", onLeave);
    window.addEventListener("blur", onLeave);

    const makeAnt = () => {
      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const sizeMin = isMobile ? 1.05 : 1.35;
      const sizeMax = isMobile ? 1.55 : 2.0;
      return {
        x: rand(0, width),
        y: rand(0, height),
        heading: rand(0, Math.PI * 2),
        speed: rand(SPEED_MIN, SPEED_MAX),
        size: rand(sizeMin, sizeMax),
        wobble: rand(0, Math.PI * 2),
        color: ANT_COLORS[Math.floor(Math.random() * ANT_COLORS.length)],
      };
    };
    const syncCount = () => {
      const target = getAntCount();
      const ants = antsRef.current;
      while (ants.length < target) ants.push(makeAnt());
      if (ants.length > target) ants.length = target;
    };
    syncCount();

    const emitEmbers = (a, hot) => {
      const embers = embersRef.current;
      const chance = hot ? EMBER_EMIT_CHANCE * 1.6 : EMBER_EMIT_CHANCE;
      if (Math.random() > chance) return;
      const back = a.heading + Math.PI;
      const tailDist = 7 * a.size;
      const tx = a.x + Math.cos(back) * tailDist;
      const ty = a.y + Math.sin(back) * tailDist;
      const jitter = rand(-0.25, 0.25);
      const ang = back + jitter;
      const sp = rand(0.08, 0.22);
      embers.push({
        x: tx + rand(-0.5, 0.5),
        y: ty + rand(-0.5, 0.5),
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp - rand(0.05, 0.15),
        life: 0,
        maxLife: rand(EMBER_LIFE_MIN, EMBER_LIFE_MAX),
        size: rand(0.4, 0.9) * a.size,
        hot: hot ? 1 : 0,
      });
      if (embers.length > MAX_EMBERS) {
        embers.splice(0, embers.length - MAX_EMBERS);
      }
    };

    const drawAnt = (a) => {
      ctx.save();
      ctx.translate(a.x, a.y);
      ctx.rotate(a.heading);
      const s = a.size;
      ctx.scale(s, s);

      const isLight = themeRef.current === "light";
      const adjust = (c) => (isLight ? Math.min(Math.round(70 + c * 0.35), 165) : c);
      const cr = adjust(a.color[0]);
      const cg = adjust(a.color[1]);
      const cb = adjust(a.color[2]);
      const strokeA = isLight ? 0.32 : 0.16;
      const fillA = isLight ? 0.34 : 0.2;
      ctx.strokeStyle = `rgba(${cr}, ${cg}, ${cb}, ${strokeA})`;
      ctx.lineWidth = isLight ? 0.7 : 0.7;
      ctx.lineCap = "round";
      const legOffset = Math.sin(a.wobble) * 1.2;
      ctx.beginPath();
      ctx.moveTo(2, -1); ctx.lineTo(-1 + legOffset, -4);
      ctx.moveTo(2, 1);  ctx.lineTo(-1 + legOffset, 4);
      ctx.moveTo(0, -1); ctx.lineTo(-3 - legOffset, -4);
      ctx.moveTo(0, 1);  ctx.lineTo(-3 - legOffset, 4);
      ctx.moveTo(-3, -1); ctx.lineTo(-6 + legOffset, -4);
      ctx.moveTo(-3, 1);  ctx.lineTo(-6 + legOffset, 4);
      ctx.moveTo(5, -0.6); ctx.lineTo(8, -2.5);
      ctx.moveTo(5, 0.6);  ctx.lineTo(8, 2.5);
      ctx.stroke();

      ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${fillA})`;
      ctx.beginPath(); ctx.ellipse(5, 0, 2.6, 2.0, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(1, 0, 1.7, 1.5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(-4, 0, 3.2, 2.3, 0, 0, Math.PI * 2); ctx.fill();

      ctx.restore();
    };

    const drawEmbers = () => {
      const embers = embersRef.current;
      const isLight = themeRef.current === "light";
      ctx.save();
      ctx.globalCompositeOperation = isLight ? "source-over" : "lighter";
      const alphaScale = isLight ? 0.5 : 1;
      for (let i = embers.length - 1; i >= 0; i--) {
        const p = embers[i];
        p.life += 1;
        if (p.life >= p.maxLife) {
          embers.splice(i, 1);
          continue;
        }
        p.vy -= 0.012;
        p.vx *= 0.985;
        p.vy *= 0.985;
        p.x += p.vx;
        p.y += p.vy;

        const t = p.life / p.maxLife;
        const alpha = (1 - t) * (0.35 + 0.2 * p.hot) * alphaScale;
        const r = p.size * (1.4 + t * 1.4);

        let cr, cg, cb;
        if (t < 0.35) {
          cr = 255; cg = 220; cb = 140;
        } else if (t < 0.7) {
          cr = 255; cg = 150; cb = 50;
        } else {
          cr = 220; cg = 80; cb = 30;
        }

        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
        grad.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, ${alpha})`);
        grad.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    const step = () => {
      ctx.clearRect(0, 0, width, height);
      const ants = antsRef.current;

      for (let i = 0; i < ants.length; i++) {
        const a = ants[i];
        let turn = (Math.random() - 0.5) * WANDER;

        for (let j = 0; j < ants.length; j++) {
          if (i === j) continue;
          const b = ants[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < AVOID_RADIUS * AVOID_RADIUS && d2 > 0.1) {
            const angleToOther = Math.atan2(dy, dx);
            let diff = angleToOther - a.heading;
            while (diff > Math.PI) diff -= Math.PI * 2;
            while (diff < -Math.PI) diff += Math.PI * 2;
            const strength = 1 - Math.sqrt(d2) / AVOID_RADIUS;
            turn -= Math.sign(diff || 1) * AVOID_TURN * strength;
          }
        }

        let speedMul = 1;
        let hot = false;
        if (mouse.active) {
          const mdx = mouse.x - a.x;
          const mdy = mouse.y - a.y;
          const md2 = mdx * mdx + mdy * mdy;
          if (md2 < MOUSE_RADIUS * MOUSE_RADIUS && md2 > 0.1) {
            const angleToMouse = Math.atan2(mdy, mdx);
            let diff = angleToMouse - a.heading;
            while (diff > Math.PI) diff -= Math.PI * 2;
            while (diff < -Math.PI) diff += Math.PI * 2;
            const strength = 1 - Math.sqrt(md2) / MOUSE_RADIUS;
            turn -= Math.sign(diff || 1) * MOUSE_TURN * strength;
            speedMul += MOUSE_SPEED_BOOST * strength;
            hot = true;
          }
        }

        a.heading += turn;
        a.wobble += 0.35;
        a.x += Math.cos(a.heading) * a.speed * speedMul;
        a.y += Math.sin(a.heading) * a.speed * speedMul;

        const m = 30;
        if (a.x < -m) a.x = width + m;
        else if (a.x > width + m) a.x = -m;
        if (a.y < -m) a.y = height + m;
        else if (a.y > height + m) a.y = -m;

        emitEmbers(a, hot);
      }

      drawEmbers();
      for (let i = 0; i < ants.length; i++) drawAnt(ants[i]);

      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    const onVis = () => {
      if (document.hidden) cancelAnimationFrame(rafRef.current);
      else rafRef.current = requestAnimationFrame(step);
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(rafRef.current);
      themeObs.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
      window.removeEventListener("blur", onLeave);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return <canvas ref={canvasRef} className="ant-bg" aria-hidden="true" />;
};

export default Ants;
